import React from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { StatusTag } from '../../components/StatusTag';
import { TypeMark } from '../../components/DeliverableGroups';
import { CLIENT_STAGE_ORDER, deliverableStage, metaLabel, stageHeading } from '../../utils/deliverables';
import type { Deliverable, Project } from '../../types/portal';
interface TaggedDeliverable {
  item: Deliverable;
  episode: Project;
}
function isOpenable(item: Deliverable): boolean {
  if (item.type === 'Transcript') return true;
  const stage = deliverableStage(item.status);
  return stage === 'Review' || stage === 'Final Delivery';
}

/**
 * Every deliverable Portal has marked visible, pooled across every episode
 * of the show and grouped by stage — the same grouping a single project's
 * own Deliverables page uses (`DeliverableGroups`), just spanning every
 * episode at once instead of one project's own list.
 *
 * Added 10 Sep 2026 alongside the Show Home banner's multi-item "Start
 * Reviewing" fix: a client on a show with several episodes previously had
 * no way to see everything awaiting review in one place — only the show's
 * per-episode picker list, or opening episodes one at a time. Each row here
 * names the episode it belongs to, since a show reader can't assume "the
 * project" the way a single project's own Deliverables page can.
 */
export function ShowDeliverables() {
  const {
    activeShow,
    showProjects,
    openShowDeliverable,
    goClient
  } = usePortal();
  const pool: TaggedDeliverable[] = showProjects.filter(episode => !episode.archived).flatMap(episode => episode.deliverables.filter(item => item.visibleToClient).map(item => ({
    item,
    episode
  })));
  const groups = CLIENT_STAGE_ORDER.map(stage => ({
    stage,
    entries: pool.filter(entry => deliverableStage(entry.item.status) === stage)
  })).filter(group => group.entries.length > 0);
  return <div data-id="element-2144" data-mp="25lirlpesjlqt">
      <button type="button" onClick={() => goClient('show')} className="label group flex items-center gap-3 text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-2145" data-mp="25lsg18k6tkue">
        <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-2146" data-mp="1wmf7qj7sv2b9" />
        {activeShow ? activeShow.name : 'Show Home'}
      </button>

      <p className="label mt-10 text-mute" data-id="element-2147" data-mp="2pat8v7u6qer1">{activeShow?.type || 'Show'}</p>
      <h1 className="mt-6 font-display text-5xl leading-[0.95] tracking-tight sm:text-7xl" data-id="element-2148" data-mp="2tabrty2g4fr8">
        DELIVERABLES
      </h1>
      <p className="mt-8 max-w-2xl font-display text-xl text-charcoal" data-id="element-2149" data-mp="2fblpo40rrpw0">
        Everything across every episode of this show, grouped by where it currently sits. Open
        anything in review to watch the latest version, request changes or approve it.
      </p>

      <div className="mt-16 border-t border-rule pt-10" data-id="element-2150" data-mp="18wt6lq7vad0m">
        {groups.length === 0 ? <div className="border-t border-rule py-14" data-id="element-2151" data-mp="11bh8dg9y2r8f">
            <span className="label block text-mute" data-id="element-2152" data-mp="11e3inno7zkuc">In Motion</span>
            <p className="mt-5 max-w-sm font-display text-2xl leading-snug text-graphite" data-id="element-2153" data-mp="wcnuvnunr089">
              Deliverables are being confirmed.
            </p>
            <p className="label-sm mt-4 text-mute" data-id="element-2154" data-mp="15dcb8p7ixcob">
              They&rsquo;ll appear here the moment Portal adds them.
            </p>
          </div> : groups.map((group, groupIndex) => <section key={group.stage} className={groupIndex === 0 ? '' : 'mt-12'} data-id="element-2155" data-mp="dnhbrzpyc6lx" data-mpi={groupIndex}>
              <h3 className="label border-b border-rule pb-3 text-mute" data-id="element-2156" data-mp="remejaom9f0h">
                {stageHeading(group.stage)}
              </h3>
              <ul className="m-0 list-none p-0" data-id="element-2157" data-mp="mmkw170uus20">
                {group.entries.map(({
            item,
            episode
          }, index) => {
            const openable = isOpenable(item);
            return <li key={`${episode.id}-${item.id}`} className="border-b border-rule" data-id="element-2158" data-mp="3q3jtqjx7yeyy" data-mpi={index}>
                      <button type="button" disabled={!openable} onClick={() => openShowDeliverable(episode.id, item.id)} className={['group grid w-full grid-cols-[3.5rem_1fr] items-baseline gap-x-5 gap-y-4 py-7 text-left sm:grid-cols-[3.25rem_4.5rem_1fr_auto] sm:gap-x-6', openable ? 'cursor-pointer' : 'cursor-default'].join(' ')} data-id="element-2159" data-mp="7mqphi4pxdgc">
                        <TypeMark type={item.type} driveUrl={item.driveUrl} data-id="element-2160" data-mp="vd36dihxykj2" />
                        <span className="font-display text-3xl leading-none text-mute sm:text-4xl" data-id="element-2161" data-mp="10amjug6tet9y">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <span className="min-w-0" data-id="element-2162" data-mp="323107atpnpo9">
                          <span className={['block font-display text-2xl leading-tight transition-colors duration-150 ease-out sm:text-4xl', openable ? 'text-ink group-hover:text-graphite' : 'text-graphite'].join(' ')} data-id="element-2163" data-mp="3709idbeuztpi">
                            {item.name}
                          </span>
                          <span className="label-sm mt-3 block text-mute" data-id="element-2164" data-mp="1afbbey4ol9nd">
                            {metaLabel(item)} · {episode.name}
                          </span>
                        </span>
                        <span className="col-start-2 flex items-center gap-4 sm:col-start-4" data-id="element-2165" data-mp="2340gt9crs14x">
                          <StatusTag status={item.status} data-id="element-2166" data-mp="1y2b3fn22rbdo" />
                          {openable && <ArrowRightIcon className="hidden h-3.5 w-3.5 text-mute transition-transform duration-150 ease-out group-hover:translate-x-1 sm:block" strokeWidth={1.25} data-id="element-2167" data-mp="1t3q3loqqitpr" />}
                        </span>
                      </button>
                    </li>;
          })}
              </ul>
            </section>)}
      </div>
    </div>;
}